import { useState, useCallback, useEffect, useRef } from 'react';
import { AudioMutedOutlined, AudioOutlined } from '@ant-design/icons';
import { useASR, ASRConfig, ASRResult } from '@/hooks/useASR';
import { Button, Input, Switch, Tooltip } from 'antd';

// export type InputMode = 'voice' | 'text';

export interface UserInputProps {
  /** AI 是否正在说话 */
  aiSpeaking?: boolean;
  /** 输入模式：语音或文本 */
  // mode?: InputMode;
  /** 切换输入模式回调 */
  // onModeChange?: (mode: InputMode) => void;
  /** 发送消息回调 */
  onSend?: (text: string) => Promise<boolean>;
  /** 是否禁用输入 */
  // disabled?: boolean;
  /** 结束面试回调 */
  onEndInterview?: () => void;
  /** 面试编号 */
  interviewNumber?: number;
}

const UserInput = ({
  aiSpeaking = false,
  // mode = 'voice',
  // onModeChange,
  onSend,
  // disabled = false,
  onEndInterview,
  interviewNumber,
}: UserInputProps) => {
  const [isSending, setIsSending] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [textInputValue, setTextInputValue] = useState('');
  const [isTimeoutTooltipOpen, setIsTimeoutTooltipOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const asrProcessingRef = useRef<number>();

  // 语音识别结果处理
  const handleASRResult = useCallback((result: ASRResult) => {
    clearTimeout(asrProcessingRef.current);
    asrProcessingRef.current = setTimeout(() => {
      setIsProcessing(false);
    }, 2000);
    setIsProcessing(true);
    if (result.isFinal) {
      setTextInputValue((prev) => {
        return prev + result.text;
      });
    }
  }, []);

  // 使用 ASR Hook
  const {
    stateMessage,
    errorCode,
    isFinal,
    isMuted,
    pause: asrPause,
    continue: asrContinue,
  } = useASR({
    interviewNumber: interviewNumber || 0,
    onResult: handleASRResult,
  } as ASRConfig);

  useEffect(() => {
    if (aiSpeaking) {
      asrPause();
    } else {
      asrContinue();
    }
  }, [aiSpeaking, asrPause, asrContinue]);

  const isAsrTimeout = errorCode === 'ASR_ENGINE_TIMEOUT';

  // ASR 超时自动弹出提示
  useEffect(() => {
    if (isAsrTimeout) {
      setIsTimeoutTooltipOpen(true);
    } else {
      setIsTimeoutTooltipOpen(false);
    }
  }, [isAsrTimeout]);

  // 处理文本输入发送
  const handleSend = async () => {
    if (isSending) return;
    const text = textInputValue.trim();
    if (text && onSend) {
      setIsSending(true);
      const success = await onSend(text);
      if (success) {
        setTextInputValue('');
      }
      setIsSending(false);
    }
  };

  return (
    <div className="h-[200px] flex-shrink-0 bg-gradient-to-br from-white/95 to-slate-50/95 rounded-2xl px-5 py-4 shadow-lg border border-slate-200/80 backdrop-blur-xl z-[1]">
      <div className="w-full">
        {/* 头部区域：状态指示和结束按钮 */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-4">
            {/* 麦克风状态指示（Switch 切换静音） */}
            <div className="flex items-center gap-2">
              <Switch
                checked={!isMuted}
                onChange={(checked) => {
                  if (checked) {
                    asrContinue();
                  } else {
                    asrPause();
                  }
                }}
                checkedChildren={<AudioOutlined />}
                unCheckedChildren={<AudioMutedOutlined />}
                style={{
                  backgroundColor: isMuted ? '#6b7280' : '#ef4444',
                  borderColor: isMuted ? '#6b7280' : '#ef4444',
                }}
              />
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  isMuted ? 'bg-gray-100' : 'bg-red-50'
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    isMuted ? 'bg-gray-500' : 'animate-pulse bg-red-500'
                  }`}
                ></span>
                <span
                  className={`text-xs font-semibold ${isMuted ? 'text-gray-500' : 'text-red-500'}`}
                >
                  {isMuted ? '已静音' : '麦克风已开启'}
                </span>
              </div>
            </div>
            {/** 状态显示 */}
            <Tooltip
              title={
                isAsrTimeout ? (
                  <span className="text-xs">
                    语音识别长时间没有反馈?，
                    <button
                      type="button"
                      className="ml-1 underline text-indigo-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        asrContinue();
                        setIsTimeoutTooltipOpen(false);
                      }}
                    >
                      点我恢复
                    </button>
                  </span>
                ) : (
                  stateMessage
                )
              }
              open={isAsrTimeout ? isTimeoutTooltipOpen : undefined}
              onOpenChange={(open) => {
                if (isAsrTimeout) {
                  setIsTimeoutTooltipOpen(open);
                }
              }}
            >
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100">
                <span className="text-xs font-semibold text-gray-500">{stateMessage}</span>
              </div>
            </Tooltip>
          </div>
          {isProcessing && (
            <div className="flex items-center gap-1 h-6 ml-2">
              {/* 音频信号动画条 */}
              <span className="block w-1 h-3 bg-indigo-400 rounded-sm animate-wave1"></span>
              <span className="block w-1 h-5 bg-indigo-500 rounded-sm animate-wave2"></span>
              <span className="block w-1 h-4 bg-indigo-600 rounded-sm animate-wave3"></span>
              <span className="block w-1 h-6 bg-indigo-500 rounded-sm animate-wave2"></span>
              <span className="block w-1 h-3 bg-indigo-400 rounded-sm animate-wave1"></span>
            </div>
          )}
        </div>

        <div className="flex flex-row gap-2">
          <Input.TextArea
            autoSize={{
              maxRows: 4,
              minRows: 4,
            }}
            allowClear
            maxLength={10000}
            showCount
            placeholder="输入消息..."
            value={textInputValue}
            onChange={(e) => setTextInputValue(e.target.value)}
            disabled={aiSpeaking || isSending}
            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
            onCompositionStart={() => setIsComposing(true)} // 组合开始
            onCompositionEnd={() => setIsComposing(false)} // 组合结束
            onKeyDown={(e) => {
              // 如果只是 Enter 键，且不在组合输入状态，则发送消息
              if (e.key === 'Enter' && !isComposing) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <div className="flex flex-col gap-2">
            <Button
              type="primary"
              size="small"
              onClick={handleSend}
              disabled={aiSpeaking || isSending}
              className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              发送
            </Button>
            {/* 结束面试按钮 */}
            {onEndInterview && (
              <Button danger onClick={onEndInterview}>
                结束面试
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInput;
